/**
 * The rows of an output field mapping. The workflow decides them, not the
 * table: each field the workflow reports is one row, whose column the user
 * picks. A field that fills several columns has one row per column. Values
 * that are no field of the workflow (an element of a split list, a row
 * written to an earlier table, a fixed option) follow as rows of their own.
 */
import type { OutputMapping } from '../../services/automation';
import type { OutputMappingSchemaField } from './output-mapping-writer';

export interface OutputMappingRow<
  F extends OutputMappingSchemaField = OutputMappingSchemaField,
> {
  /** Stable while the row keeps its value and place. */
  readonly key: string;
  /**
   * `output`: a field the workflow reports, given. `other`: another value,
   * chosen in the row.
   */
  readonly kind: 'output' | 'other';
  /** The workflow's field of an `output` row. */
  readonly field?: F;
  /** The stored mapping; none while the row has no column. */
  readonly mapping?: OutputMapping;
}

export function buildOutputMappingRows<F extends OutputMappingSchemaField>(
  outputs: ReadonlyArray<F>,
  mappings: ReadonlyArray<OutputMapping>
): OutputMappingRow<F>[] {
  const reported = new Set(outputs.map(field => field.path));
  const rows: OutputMappingRow<F>[] = [];
  for (const field of outputs) {
    const own = mappings.filter(
      mapping => !mapping.fixedValue && mapping.responseField === field.path
    );
    if (own.length === 0) {
      rows.push({ key: `output:${field.path}`, kind: 'output', field });
      continue;
    }
    own.forEach((mapping, index) =>
      rows.push({
        key:
          index === 0
            ? `output:${field.path}`
            : `output:${field.path}:${mapping.targetColumnId}`,
        kind: 'output',
        field,
        mapping,
      })
    );
  }
  for (const mapping of mappings) {
    const fromWorkflow =
      !mapping.fixedValue && reported.has(mapping.responseField);
    if (fromWorkflow || (!mapping.responseField && !mapping.fixedValue))
      continue;
    rows.push({
      key: `other:${mapping.targetColumnId}`,
      kind: 'other',
      mapping,
    });
  }
  return rows;
}
